/**
 * Low-level HTTP client with retry and error handling
 */
import { getLogger } from '@qwickapps/logging';
import { MiddlewareClientError, NetworkError, AuthenticationError, ValidationError, NotFoundError, RateLimitError, ServiceUnavailableError, } from '../types/errors.js';
const log = getLogger('QwickForge.MiddlewareClient.HttpClient');
export class HttpClient {
    baseUrl;
    getAuthHeaders;
    retryOptions;
    constructor(config) {
        this.baseUrl = config.baseUrl;
        this.getAuthHeaders = config.getAuthHeaders;
        this.retryOptions = config.retryOptions;
    }
    /**
     * GET request
     */
    async get(endpoint, options) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }
    /**
     * POST request
     */
    async post(endpoint, body, options) {
        return this.request(endpoint, {
            ...options,
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined,
        });
    }
    /**
     * PATCH request
     */
    async patch(endpoint, body, options) {
        return this.request(endpoint, {
            ...options,
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined,
        });
    }
    /**
     * PUT request
     */
    async put(endpoint, body, options) {
        return this.request(endpoint, {
            ...options,
            method: 'PUT',
            body: body ? JSON.stringify(body) : undefined,
        });
    }
    /**
     * DELETE request
     */
    async delete(endpoint, options) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }
    /**
     * Core request method with retry logic
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const maxAttempts = this.retryOptions?.maxAttempts ?? 1;
        let lastError = null;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await this.executeRequest(url, options, attempt);
                return response;
            }
            catch (error) {
                lastError = error;
                // Don't retry on certain errors
                if (error instanceof AuthenticationError ||
                    error instanceof ValidationError ||
                    error instanceof NotFoundError) {
                    throw error;
                }
                // Check if we should retry
                if (attempt < maxAttempts) {
                    const delay = this.calculateBackoff(attempt);
                    log.debug('Request failed, retrying', {
                        attempt,
                        maxAttempts,
                        delayMs: delay,
                        error: error instanceof Error ? error.message : String(error),
                    });
                    await this.sleep(delay);
                }
            }
        }
        // All retries exhausted
        throw lastError || new NetworkError('Request failed after all retries');
    }
    /**
     * Execute single HTTP request
     */
    async executeRequest(url, options, attempt) {
        const startTime = Date.now();
        try {
            // Get auth headers
            const authHeaders = await this.getAuthHeaders();
            // Merge headers
            const headers = {
                'Content-Type': 'application/json',
                ...authHeaders,
                ...options.headers,
            };
            log.debug('HTTP request', {
                method: options.method || 'GET',
                url,
                attempt,
            });
            const response = await fetch(url, { ...options, headers });
            const latencyMs = Date.now() - startTime;
            // Handle non-OK responses
            if (!response.ok) {
                await this.handleErrorResponse(response, latencyMs);
            }
            // Parse response
            const contentType = response.headers.get('content-type');
            let data;
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            }
            else {
                data = (await response.text());
            }
            log.debug('HTTP response', {
                method: options.method || 'GET',
                url,
                status: response.status,
                latencyMs,
            });
            return data;
        }
        catch (error) {
            const latencyMs = Date.now() - startTime;
            // If already a MiddlewareClientError, rethrow
            if (error instanceof MiddlewareClientError) {
                throw error;
            }
            // Network or fetch error
            log.error('HTTP request failed', {
                method: options.method || 'GET',
                url,
                latencyMs,
                error: error instanceof Error ? error.message : String(error),
            });
            throw new NetworkError(`Request failed: ${error instanceof Error ? error.message : String(error)}`, { url, latencyMs });
        }
    }
    /**
     * Handle error responses based on status code
     */
    async handleErrorResponse(response, latencyMs) {
        const status = response.status;
        const statusText = response.statusText;
        // Try to parse error body
        let errorBody;
        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                errorBody = await response.json();
            }
            else {
                errorBody = await response.text();
            }
        }
        catch {
            errorBody = statusText;
        }
        // Extract error message from various response formats
        let errorMessage;
        if (typeof errorBody === 'string') {
            errorMessage = errorBody;
        }
        else if (errorBody?.message) {
            errorMessage = errorBody.message;
        }
        else if (errorBody?.error) {
            // Handle nested error objects: { error: { message: '...' } } or { error: '...' }
            if (typeof errorBody.error === 'string') {
                errorMessage = errorBody.error;
            }
            else if (errorBody.error?.message) {
                errorMessage = errorBody.error.message;
            }
            else {
                errorMessage = JSON.stringify(errorBody.error);
            }
        }
        else {
            errorMessage = statusText;
        }
        log.error('HTTP error response', {
            status,
            statusText,
            errorMessage,
            latencyMs,
        });
        // Map status codes to error types
        switch (status) {
            case 401:
                throw new AuthenticationError(errorMessage, { status, body: errorBody });
            case 400:
                throw new ValidationError(errorMessage, { status, body: errorBody });
            case 404:
                throw new NotFoundError('Resource', errorMessage);
            case 429:
                const retryAfter = response.headers.get('retry-after');
                throw new RateLimitError(retryAfter ? parseInt(retryAfter, 10) : undefined);
            case 503:
                throw new ServiceUnavailableError(errorMessage);
            default:
                throw new MiddlewareClientError(errorMessage, 'HTTP_ERROR', status, { body: errorBody });
        }
    }
    /**
     * Calculate exponential backoff delay
     */
    calculateBackoff(attempt) {
        const initialDelay = this.retryOptions?.initialDelayMs ?? 1000;
        const maxDelay = this.retryOptions?.maxDelayMs ?? 10000;
        // Exponential backoff: initialDelay * 2^(attempt-1)
        const delay = initialDelay * Math.pow(2, attempt - 1);
        // Cap at maxDelay
        return Math.min(delay, maxDelay);
    }
    /**
     * Sleep for specified milliseconds
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
//# sourceMappingURL=HttpClient.js.map